import { api } from "@/api/client";

export type TripStatus = "planned" | "completed" | "cancelled";

export interface RefName {
  ref: string;
  name: string;
}

// ── Груз: заказы (покупателя/поставщику) ──────────────────────────────────

export type OrderType = "customer" | "supplier";

export interface OrderCard {
  ref: string;
  number: string;
  date: string;
  order_type: OrderType;
  organization_ref: string;
  organization_name: string;
  kontragent_ref: string;
  kontragent_name: string;
  amount: number;
}

export interface OrderListResponse {
  items: OrderCard[];
  has_more: boolean;
}

export async function searchOrders(
  tripId: string,
  params: { role: string; organization_ref: string; q?: string },
): Promise<OrderListResponse> {
  const { data } = await api.get<OrderListResponse>(
    `/logistics/trips/${tripId}/order-search`,
    {
      params: {
        role: params.role,
        organization_ref: params.organization_ref,
        q: params.q || undefined,
      },
    },
  );
  return data;
}

export async function listCarriers(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/logistics/kontragents", {
    params: { q: q || undefined },
  });
  return data;
}

export async function listLogisticsOrganizations(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/logistics/organizations", {
    params: { q: q || undefined },
  });
  return data;
}

// ── Рейсы ─────────────────────────────────────────────────────────────────

export interface StopOrderView {
  id: string;
  order_type: OrderType;
  order_ref: string;
  order_number: string | null;
  order_date: string | null;
  organization_name: string | null;
  kontragent_name: string | null;
  amount: number | null;
}

export type StopRole = "shipment" | "loading" | "internal";
export type StopCategory =
  | "b2b"
  | "mp"
  | "raw"
  | "general"
  | "product_dev"
  | "production"
  | "marketing"
  | "office";
export type StopStatus = "pending" | "done" | "returned";

export interface StopView {
  id: string;
  position: number;
  label: string | null;
  address: string | null;
  comment: string | null;
  timeslot_from: string | null;
  timeslot_to: string | null;
  lat: string | null;
  lon: string | null;
  role: StopRole;
  category: StopCategory | null;
  organization_ref: string | null;
  organization_name: string | null;
  status: StopStatus;
  delivery_cost: number | null;
  waiting_fee: number | null;
  return_fee: number | null;
  weight_kg: number | null;
  pallets: number | null;
  items_count: number | null;
  boxes: number | null;
  orders: StopOrderView[];
}

export interface TripCard {
  id: string;
  display_number: number | null;
  status: TripStatus;
  carrier_kontragent_name: string | null;
  driver_name: string | null;
  vehicle_plate: string | null;
  planned_date: string | null;
  stops_count: number;
  orders_count: number;
  total_cost: number;
  created_at: string;
}

export interface TripListResponse {
  items: TripCard[];
  has_more: boolean;
}

export interface TripDetail {
  id: string;
  display_number: number | null;
  status: TripStatus;
  carrier_kontragent_ref: string | null;
  carrier_kontragent_name: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  vehicle_plate: string | null;
  vehicle_model: string | null;
  vehicle_type: string | null;
  planned_date: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
  total_delivery: number;
  total_waiting: number;
  total_returns: number;
  total_cost: number;
  stops: StopView[];
}

export interface TripCreateIn {
  carrier_kontragent_ref?: string | null;
  carrier_kontragent_name?: string | null;
  driver_name?: string | null;
  driver_phone?: string | null;
  vehicle_plate?: string | null;
  vehicle_model?: string | null;
  vehicle_type?: string | null;
  planned_date?: string | null;
  comment?: string | null;
}

export type TripUpdateIn = TripCreateIn & { status?: TripStatus };

export interface TripListParams {
  q?: string;
  status?: string;
  skip?: number;
}

export async function listTrips(params: TripListParams): Promise<TripListResponse> {
  const { data } = await api.get<TripListResponse>("/logistics/trips", {
    params: {
      q: params.q || undefined,
      status: params.status || undefined,
      skip: params.skip || undefined,
    },
  });
  return data;
}

export async function createTrip(body: TripCreateIn): Promise<TripDetail> {
  const { data } = await api.post<TripDetail>("/logistics/trips", body);
  return data;
}

export async function getTrip(id: string): Promise<TripDetail> {
  const { data } = await api.get<TripDetail>(`/logistics/trips/${id}`);
  return data;
}

export async function updateTrip(id: string, patch: TripUpdateIn): Promise<TripDetail> {
  const { data } = await api.patch<TripDetail>(`/logistics/trips/${id}`, patch);
  return data;
}

export async function deleteTrip(id: string): Promise<void> {
  await api.delete(`/logistics/trips/${id}`);
}

// ── Точки ─────────────────────────────────────────────────────────────────

export interface StopIn {
  label?: string | null;
  address?: string | null;
  comment?: string | null;
  timeslot_from?: string | null;
  timeslot_to?: string | null;
  lat?: string | null;
  lon?: string | null;
  role?: StopRole;
  category?: StopCategory | null;
  organization_ref?: string | null;
  organization_name?: string | null;
  status?: StopStatus;
  delivery_cost?: number | null;
  waiting_fee?: number | null;
  return_fee?: number | null;
  weight_kg?: number | null;
  pallets?: number | null;
  items_count?: number | null;
  boxes?: number | null;
}

export async function addStop(tripId: string, body: StopIn): Promise<TripDetail> {
  const { data } = await api.post<TripDetail>(`/logistics/trips/${tripId}/stops`, body);
  return data;
}

export async function updateStop(
  tripId: string,
  stopId: string,
  patch: StopIn,
): Promise<TripDetail> {
  const { data } = await api.patch<TripDetail>(
    `/logistics/trips/${tripId}/stops/${stopId}`,
    patch,
  );
  return data;
}

export async function deleteStop(tripId: string, stopId: string): Promise<TripDetail> {
  const { data } = await api.delete<TripDetail>(
    `/logistics/trips/${tripId}/stops/${stopId}`,
  );
  return data;
}

export async function reorderStops(
  tripId: string,
  stopIds: string[],
): Promise<TripDetail> {
  const { data } = await api.put<TripDetail>(`/logistics/trips/${tripId}/stops/order`, {
    stop_ids: stopIds,
  });
  return data;
}

export async function attachOrder(
  tripId: string,
  stopId: string,
  orderType: OrderType,
  orderRef: string,
): Promise<TripDetail> {
  const { data } = await api.post<TripDetail>(
    `/logistics/trips/${tripId}/stops/${stopId}/orders`,
    { order_type: orderType, order_ref: orderRef },
  );
  return data;
}

export async function detachOrder(
  tripId: string,
  stopId: string,
  orderId: string,
): Promise<TripDetail> {
  const { data } = await api.delete<TripDetail>(
    `/logistics/trips/${tripId}/stops/${stopId}/orders/${orderId}`,
  );
  return data;
}

// ── Адресная книга / подсказки ────────────────────────────────────────────

export type AddressKind = "client" | "own";

export interface SavedAddress {
  id: string;
  alias: string;
  address: string;
  kind: AddressKind;
  lat: string | null;
  lon: string | null;
}

export async function listAddresses(q?: string, kind?: string): Promise<SavedAddress[]> {
  const { data } = await api.get<SavedAddress[]>("/logistics/addresses", {
    params: { q: q || undefined, kind: kind || undefined },
  });
  return data;
}

export async function createAddress(
  alias: string,
  address: string,
  opts?: { kind?: AddressKind; lat?: string | null; lon?: string | null },
): Promise<SavedAddress> {
  const { data } = await api.post<SavedAddress>("/logistics/addresses", {
    alias,
    address,
    kind: opts?.kind || "client",
    lat: opts?.lat || null,
    lon: opts?.lon || null,
  });
  return data;
}

export async function updateAddress(
  id: string,
  patch: { alias?: string; address?: string; kind?: AddressKind },
): Promise<SavedAddress> {
  const { data } = await api.patch<SavedAddress>(`/logistics/addresses/${id}`, patch);
  return data;
}

export async function deleteAddress(id: string): Promise<void> {
  await api.delete(`/logistics/addresses/${id}`);
}

export interface DriverSuggestion {
  driver_name: string;
  driver_phone: string;
  vehicle_plate: string;
  vehicle_model: string;
}

export async function driverSuggest(q?: string): Promise<DriverSuggestion[]> {
  const { data } = await api.get<DriverSuggestion[]>("/logistics/driver-suggest", {
    params: { q: q || undefined },
  });
  return data;
}

// ── Верификация адреса (внешний сервис DaData) ────────────────────────────

export interface AddressSuggestion {
  value: string;
  unrestricted_value: string;
  geo_lat: string | null;
  geo_lon: string | null;
}

export async function addressSuggest(q: string): Promise<AddressSuggestion[]> {
  const { data } = await api.get<AddressSuggestion[]>("/logistics/address-suggest", {
    params: { q },
  });
  return data;
}

// ── Карта (Яндекс) ────────────────────────────────────────────────────────

export interface MapConfig {
  configured: boolean;
  apikey: string;
}

export async function getMapConfig(): Promise<MapConfig> {
  const { data } = await api.get<MapConfig>("/logistics/map-config");
  return data;
}

export interface TripRoute {
  ok: boolean;
  distance_km: number | null;
  duration_min: number | null;
  geometry: number[][]; // [[lat, lon], ...] по дорогам
  legs_km: number[]; // дистанция плеч между точками
  message: string;
}

export async function getTripRoute(id: string): Promise<TripRoute> {
  const { data } = await api.get<TripRoute>(`/logistics/trips/${id}/route`);
  return data;
}

export interface CargoFromOrders {
  items_count: number;
  weight_kg: number;
  orders_used: number;
  missing_weight: string[];
}

export async function getCargoFromOrders(
  tripId: string,
  stopId: string,
): Promise<CargoFromOrders> {
  const { data } = await api.get<CargoFromOrders>(
    `/logistics/trips/${tripId}/stops/${stopId}/cargo-from-orders`,
  );
  return data;
}
